import React, { useCallback, useState } from 'react';
import {
    Box,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import { Close, Pause, PlayArrow } from '@mui/icons-material';
import ReplayPlayer from './ReplayPlayer';
import { API_ENDPOINTS } from '../../utils/config';
import { ARCADE_THEME } from './arcadeTheme';

const THEME = ARCADE_THEME;

export function leaderboardRunId(entry) {
    if (!entry || typeof entry !== 'object') return null;
    const id = entry._id || entry.run_id || entry.id;
    return id ? String(id) : null;
}

export function useArcadeReplay() {
    const [open, setOpen] = useState(false);
    const [run, setRun] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [startLoop, setStartLoop] = useState(false);

    const closeReplay = useCallback(() => {
        setOpen(false);
        setRun(null);
        setError(null);
        setLoading(false);
        setStartLoop(false);
    }, []);

    const openReplay = useCallback(async (entry, { loop = false } = {}) => {
        const runId = leaderboardRunId(entry);
        if (!runId) return;
        setOpen(true);
        setLoading(true);
        setError(null);
        setRun(null);
        setStartLoop(Boolean(loop));
        try {
            const res = await fetch(API_ENDPOINTS.RUN_DETAIL(runId));
            if (!res.ok) throw new Error(`status ${res.status}`);
            const data = await res.json();
            setRun(data.run || data.entry || data);
        } catch (err) {
            console.warn('Arcade replay fetch failed', runId, err);
            setError('Unable to load this replay.');
        } finally {
            setLoading(false);
        }
    }, []);

    return { open, run, loading, error, startLoop, openReplay, closeReplay };
}

export function ArcadeReplayDialog({ open, loading, error, run, startLoop = false, onClose }) {
    return (
        <Dialog
            open={Boolean(open)}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    bgcolor: THEME.cardBg,
                    border: `1px solid ${THEME.border}`,
                    backgroundImage: 'none',
                },
            }}
        >
            <DialogTitle
                sx={{
                    color: 'white',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pr: 1,
                }}
            >
                Replay
                <IconButton onClick={onClose} sx={{ color: THEME.secondary }} aria-label="Close replay">
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent sx={{ pb: 3 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress size={32} sx={{ color: THEME.primary }} />
                    </Box>
                ) : error ? (
                    <Typography sx={{ color: THEME.accent, textAlign: 'center', py: 3 }}>
                        {error}
                    </Typography>
                ) : run ? (
                    <ReplayPlayer run={run} initialLoop={startLoop} />
                ) : (
                    <Typography sx={{ color: THEME.secondary, textAlign: 'center', py: 3 }}>
                        No run data.
                    </Typography>
                )}
            </DialogContent>
        </Dialog>
    );
}

export function LeaderboardWatchButton({
    entry,
    onWatch,
    accentColor = THEME.primary,
    playing = false,
    playLabel = 'Play',
    pauseLabel = 'Pause',
    tooltipLabel = null,
}) {
    const runId = leaderboardRunId(entry);
    if (!runId) return null;
    const label = playing ? pauseLabel : playLabel;
    return (
        <Tooltip title={tooltipLabel || label}>
            <IconButton
                size="small"
                data-testid="leaderboard-play"
                aria-label={label}
                onClick={() => onWatch(entry)}
                sx={{ color: accentColor, flexShrink: 0 }}
            >
                {playing ? <Pause fontSize="small" /> : <PlayArrow fontSize="small" />}
            </IconButton>
        </Tooltip>
    );
}

export const LAST_RUN_LOOP_BUTTON_SX = (accentColor = THEME.primary) => ({
    minWidth: 28,
    width: 28,
    height: 28,
    p: 0,
    color: accentColor,
    borderRadius: 1.5,
});
